import { NativeStackHeaderProps, Stack, useRouter } from 'expo-router';
import React from 'react';
import { useTranslation } from 'react-i18next';
import { StyleSheet, View } from 'react-native';
import { useSafeAreaInsets } from 'react-native-safe-area-context';
import ShowMoreAccordian from 'shared/components/ShowMoreAccordian/ShowMoreAccordion';
import HairLineDivider from 'shared/components/vxl/divider/HairlineDivider/HairLineDivider';
import ListItem from 'shared/components/vxl/ListItem/ListItem';
import ExpoNavigationBar from 'shared/components/vxl/navigation/ExpoNavigationBar/ExpoNavigationBar';
import BodyXSText from 'shared/components/vxl/typography/primary/body/BodyXSText';
import { usePressableAnalytics } from 'shared/hooks/analytics/usePressableAnalytics';
import { useViewDidFocusScreenAnalyticsEvent } from 'shared/hooks/analytics/useViewDidFocusAnalytics';
import { useStatsig } from 'shared/hooks/statsig/useStatsig';
import ActionScreen from 'shared/patterns/vxl/ActionScreen/ActionScreen';
import { FeatureFlagKey } from 'shared/providers/statsig/StatsigProvider.d';
import { VXLComponentId } from 'shared/renderer/types';
import theme from 'shared/styles/theme';

const BELIEVE_DAY_0_ACCORDION_MAX_LENGTH = 92;
const BELIEVE_ACCORDION_MAX_LENGTH = 170;

const QualifyScreen = () => {
  const router = useRouter();
  const { t: translatedText } = useTranslation('believe', {
    keyPrefix: 'qualifyString',
  });

  const { isFeatureEnabled } = useStatsig();
  const isBelieveDay0M1 = isFeatureEnabled(FeatureFlagKey.BELIEVE_DAY_0_M1);
  const isBelieveDay0M2 = isFeatureEnabled(FeatureFlagKey.BELIEVE_DAY_0_M2);
  const insets = useSafeAreaInsets();

  const handlePressAnalytics = usePressableAnalytics({
    analyticsId: 'addMoney',
  });

  useViewDidFocusScreenAnalyticsEvent({
    key: 'believeQualifyDetails',
    customAttributes: {
      subcategory: isBelieveDay0M1
        ? 'believe_day_0_application'
        : 'believe_application',
    },
  });

  const handlePressAddMoney = (): void => {
    router.push('/believe-funding');
  };

  return (
    <View
      style={[
        styles.container,
        isBelieveDay0M1 && { paddingBottom: insets.bottom },
      ]}
    >
      <Stack.Screen
        options={{
          headerBackVisible: true,
          headerShown: true,
          header: (props: NativeStackHeaderProps) => (
            <ExpoNavigationBar {...props} />
          ),
        }}
      />
      <ActionScreen
        includeDefaultPadding={false}
        primaryButton={
          isBelieveDay0M1
            ? undefined
            : {
                label: translatedText('addButton'),
                onPress: () => {
                  handlePressAnalytics();
                  handlePressAddMoney();
                },
              }
        }
        titleText={translatedText('title')}
        titleTextStyle="extra-large"
        visual={
          isBelieveDay0M2
            ? 'BELIEVE_DARK_CARD_A_ANIMATED'
            : 'BELIEVE_CARD_A_ANIMATED'
        }
        bodyContent={
          <View style={styles.body}>
            <ListItem
              leadingAccessory={{
                id: 'directDeposit',
                name: VXLComponentId.ICON_AVATAR,
                props: {
                  iconKey: 'DIRECT_DEPOSIT',
                },
              }}
              primaryCopy={translatedText(
                isBelieveDay0M1 ? 'list.believeDay0One' : 'list.one',
              )}
              primaryNumberOfLines={3}
              showsChevron={false}
            />
            <View style={styles.divider}>
              <HairLineDivider />
            </View>
            <ListItem
              leadingAccessory={{
                id: 'transfer',
                name: VXLComponentId.ICON_AVATAR,
                props: {
                  iconKey: isBelieveDay0M1 ? 'CLOCK' : 'TRANSFER',
                },
              }}
              primaryCopy={translatedText(
                isBelieveDay0M1 ? 'list.believeDay0Two' : 'list.two',
              )}
              primaryNumberOfLines={3}
              showsChevron={false}
            />
            {!isBelieveDay0M1 && (
              <View style={styles.divider}>
                <HairLineDivider />
              </View>
            )}
            {!isBelieveDay0M1 && (
              <>
                <ListItem
                  leadingAccessory={{
                    id: 'info',
                    name: VXLComponentId.ICON_AVATAR,
                    props: {
                      iconKey: 'INFO',
                    },
                  }}
                  primaryCopy={translatedText('list.three')}
                  primaryNumberOfLines={3}
                  showsChevron={false}
                />
                <View style={styles.divider}>
                  <HairLineDivider />
                </View>
              </>
            )}
            {isBelieveDay0M1 && (
              <BodyXSText style={styles.eligibilityText}>
                {translatedText('believeDay0EligibilityText')}
              </BodyXSText>
            )}
            <View
              style={[
                styles.accordion,
                { marginBottom: insets.bottom + theme.spacing.m },
              ]}
            >
              <ShowMoreAccordian
                text={translatedText(
                  isBelieveDay0M1 ? 'believeDay0TandC' : 'mainText',
                )}
                maxLength={
                  !isBelieveDay0M1
                    ? BELIEVE_ACCORDION_MAX_LENGTH
                    : BELIEVE_DAY_0_ACCORDION_MAX_LENGTH
                }
              />
            </View>
          </View>
        }
      />
    </View>
  );
};

const styles = StyleSheet.create({
  accordion: {
    marginTop: theme.spacing.m,
    marginHorizontal: theme.spacing.m,
  },
  divider: {
    paddingHorizontal: theme.spacing.m,
  },
  eligibilityText: {
    marginTop: theme.spacing.m,
    marginHorizontal: theme.spacing.m,
  },
  body: {
    flex: 1,
  },
  container: {
    backgroundColor: theme.color.background.primary,
    flex: 1,
  },
});

export default QualifyScreen;
